import ErrorDialog from "@/components/ErrorDialog"
import UsernameMark from "@/components/UsernameMark"
import { useAuth } from "@/hooks/useAuth"
import { useAuthModal } from "@/hooks/useAuthModal"
import { postPath, timeAgo } from "@/lib/filters"
import { publicProfilesFor } from "@/lib/profile"
import { privacyMarkNotificationsRead, privacyNotifications, privacyPublicProfiles } from "@/lib/privacy"
import { groupNotifications, listBlockedIds } from "@/lib/social"
import type { AppNotification, Profile } from "@/types"
import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"

const TYPE_TEXT: Record<AppNotification["type"], string> = {
  like: "liked your post",
  comment: "commented on your post",
  reply: "replied to your comment",
  follow: "started following you",
  remix: "remixed your post",
  mention: "mentioned you",
}

export default function NotificationsPage() {
  const { user, isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const [notes, setNotes] = useState<AppNotification[]>([])
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const markedRef = useRef(false)

  useEffect(() => {
    markedRef.current = false
    if (!isAuthenticated || !user?.userId) {
      openAuth()
      setLoading(false)
      return
    }
    const myId = user.userId
    const load = async () => {
      setLoading(true)
      try {
        const [notesList, blocked] = await Promise.all([
          privacyNotifications(),
          listBlockedIds(myId),
        ])
        const blockedSet = new Set(blocked)
        const mine = notesList.filter((item) => item.recipientId === myId && !blockedSet.has(item.actorId))
        const actorIds = Array.from(new Set(mine.map((item) => item.actorId).filter(Boolean)))
        const people = actorIds.length ? await privacyPublicProfiles({ userIds: actorIds }) : []
        setNotes(mine)
        setProfiles(publicProfilesFor(people, actorIds))
        if (!markedRef.current) {
          markedRef.current = true
          const unread = mine.filter((item) => !item.read)
          if (unread.length) {
            privacyMarkNotificationsRead().catch((err) => console.error("Mark read failed:", err))
          }
        }
      } catch (err) {
        console.error("Notifications load failed:", err)
        setError("Could not load notifications.")
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isAuthenticated, user?.userId, openAuth])

  if (!isAuthenticated || !user?.userId) {
    return <p className="px-4 py-10 text-center text-sm text-[#6E6E6E]">Sign in to see your notifications.</p>
  }

  const profileMap = new Map(profiles.map((profile) => [profile.userId, profile]))
  const groups = groupNotifications(notes)

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 lg:px-8">
      <h1 className="font-[Syne] text-2xl font-bold">Notifications</h1>
      <p className="mt-1 text-sm text-[#A8A8A8]">Likes, comments, follows, remixes, and mentions.</p>
      <div className="mt-5 divide-y divide-[#1F1F1F] rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[#6E6E6E]">Loading notifications...</p>
        ) : groups.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-[#6E6E6E]">No notifications yet.</p>
        ) : (
          groups.map((group) => {
            const actor = profileMap.get(group.newest.actorId)
            const target = group.newest.postId ? postPath(group.newest.postId) : `/u/${group.newest.actorId}`
            const extra = group.count - 1
            const suffix = extra > 0 ? ` and ${extra} other${extra > 1 ? "s" : ""}` : ""
            return (
              <Link
                key={group.key}
                to={target}
                className={`flex items-start justify-between gap-3 px-4 py-3 ${group.unread ? "bg-[#00FF66]/5" : ""}`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm">
                    <UsernameMark username={actor?.username} userId={group.newest.actorId} prefix={false} />
                    {suffix} {TYPE_TEXT[group.newest.type] || "interacted with your post"}
                  </p>
                  <p className="mt-1 text-[11px] text-[#6E6E6E]">{timeAgo(group.newest.createdAt)}</p>
                </div>
                {group.unread ? <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#00FF66]" /> : null}
              </Link>
            )
          })
        )}
      </div>
      {error ? <ErrorDialog message={error} onClose={() => setError("")} /> : null}
    </div>
  )
}
